import React, { useEffect, useRef } from 'react';
import { ChevronDown, XIcon } from 'lucide-react';

import type { TicketActivityRow } from '@/api/tickets/useGetTicketActivity';
import type { Ticket } from '@/api/tickets/useGetTickets';
import { Member } from '@/models/AudienceByWorkspace';

type AudienceLinkItem = NonNullable<Ticket['audienceLinks']>[number];

function optionalLinkId(v: unknown): string | undefined {
  if (v == null || v === '') return undefined;
  return String(v);
}

function nullableLinkString(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

export function dueDateToDateInputValue(dueDate: string | undefined): string {
  if (!dueDate) return '';
  const trimmed = dueDate.trim();
  const ymd = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed);
  if (ymd) return ymd[1];
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function normalizeAudienceLinks(raw: unknown): Ticket['audienceLinks'] {
  if (!Array.isArray(raw)) return undefined;
  const mapped: AudienceLinkItem[] = raw
    .map((item: Record<string, unknown>) => {
      const audienceMemberId = String(item.audienceMemberId ?? item.AudienceMemberId ?? '');
      return {
        id: optionalLinkId(item.id),
        audienceMemberId,
        audienceMemberEmail: nullableLinkString(item.audienceMemberEmail ?? item.AudienceMemberEmail),
        audienceMemberName: nullableLinkString(item.audienceMemberName ?? item.AudienceMemberName),
      };
    })
    .filter((x) => x.audienceMemberId.length > 0);
  return mapped.length > 0 ? mapped : undefined;
}

export function audienceLinkLabel(link: {
  audienceMemberName?: string | null;
  audienceMemberEmail?: string | null;
  audienceMemberId: string;
}) {
  const n = link.audienceMemberName?.trim();
  const e = link.audienceMemberEmail?.trim();
  if (n) return n;
  if (e) return e;
  return link.audienceMemberId;
}

export type AudienceMemberListPickerProps = {
  members: Member[];
  query: string;
  onQueryChange: (q: string) => void;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSelectMember: (memberId: string) => void;
  containerRef: React.Ref<HTMLDivElement>;
  collapsedLabel: string;
  compact?: boolean;
};

/** Click the row to open; type in the field to narrow the list; pick a row to add. */
export function AudienceMemberListPicker({
  members,
  query,
  onQueryChange,
  isOpen,
  onOpen,
  onClose,
  onSelectMember,
  containerRef,
  collapsedLabel,
  compact,
}: AudienceMemberListPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const textSize = compact ? 'text-xs' : 'text-sm';
  const pad = compact ? 'px-2 py-1.5' : 'px-3 py-2';

  return (
    <div ref={containerRef} className="relative z-20 w-full min-w-0 max-w-full">
      {!isOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className={`w-full flex items-center justify-between gap-2 ${pad} border border-custom-mediumSilver rounded-lg bg-white text-left text-custom-darkSilver hover:bg-custom-silver/40 ${textSize}`}
        >
          <span className="truncate">{collapsedLabel}</span>
          <ChevronDown className="w-4 h-4 shrink-0 opacity-60" aria-hidden />
        </button>
      ) : (
        <div
          className={`w-full min-w-0 max-w-full border border-custom-mediumSilver rounded-lg bg-white shadow-lg overflow-hidden ${textSize}`}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div
            className={`flex items-center gap-1 border-b border-custom-mediumSilver ${
              compact ? 'px-1 py-0.5' : 'px-2 py-1'
            }`}
          >
            <input
              ref={inputRef}
              type="search"
              autoComplete="off"
              placeholder="Type to filter…"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              className={`flex-1 min-w-0 ${pad} border-0 rounded focus:ring-0 focus:outline-none bg-transparent text-custom-black placeholder:text-custom-darkSilver`}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  onClose();
                }
              }}
            />
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-custom-darkSilver hover:text-custom-black shrink-0 rounded"
              aria-label="Close list"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
          <ul className="max-h-48 overflow-y-auto py-1">
            {members.length === 0 ? (
              <li className={`${pad} text-custom-darkSilver`}>No matching members</li>
            ) : (
              members.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className={`w-full text-left ${pad} hover:bg-custom-silver/80 ${textSize}`}
                    onClick={() => onSelectMember(m.id)}
                  >
                    <span className="font-medium text-custom-black">{m.name || '—'}</span>
                    {m.email ? <span className="block text-custom-darkSilver truncate">{m.email}</span> : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export function formatTicketActivityActor(
  row: Pick<TicketActivityRow, 'createdBy' | 'createdByName' | 'createdByEmail'>,
): string {
  const name = row.createdByName?.trim();
  const email = row.createdByEmail?.trim();
  if (name && email) return `${name} (${email})`;
  if (email) return email;
  if (name) return name;
  return row.createdBy;
}
